class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.bindGrizzCartActions();
      this.checkSoldOutItems();
      this.showAddedMessageIfNeeded();
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function' && containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;

      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html) sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.dataset.grizzJustAdded = 'true';

      this.open();
      this.showAddedMessage();
      this.bindGrizzCartActions();
      this.checkSoldOutItems();
    }, 50);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showAddedMessageIfNeeded() {
    if (this.dataset.grizzJustAdded === 'true') {
      this.showAddedMessage();
    }
  }

  showAddedMessage() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return;

    let message = drawer.querySelector('.grizz-cart-added-message');

    if (!message) {
      message = document.createElement('div');
      message.className = 'grizz-cart-added-message';
      message.innerHTML = `
        <span class="grizz-cart-added-message__icon">✓</span>
        <span>The article has been added to the shopping cart.</span>
      `;

      const header = drawer.querySelector('.drawer__header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(message, header.nextSibling);
      }
    }

    message.classList.add('is-visible');

    clearTimeout(this.grizzAddedTimer);
    this.grizzAddedTimer = setTimeout(() => {
      message.classList.remove('is-visible');
      this.dataset.grizzJustAdded = 'false';
    }, 4500);
  }

  checkSoldOutItems() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return false;

    const items = drawer.querySelectorAll('.cart-item');
    let hasSoldOut = false;

    items.forEach((item) => {
      if (item.dataset.soldOut === 'true') {
        hasSoldOut = true;
        item.classList.add('grizz-sold-out-item');
      }
    });

    const warning = drawer.querySelector('.grizz-cart-sold-warning');

    if (warning) {
      const close = warning.querySelector('.grizz-cart-sold-warning__close');

      if (close && close.dataset.grizzBound !== 'true') {
        close.dataset.grizzBound = 'true';

        close.addEventListener('click', () => {
          warning.remove();
        });
      }
    }

    return hasSoldOut;
  }

  bindGrizzCartActions() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return;

    const checkoutButton = drawer.querySelector('#CartDrawer-Checkout');

    if (checkoutButton && checkoutButton.dataset.grizzCheckoutBound !== 'true') {
      checkoutButton.dataset.grizzCheckoutBound = 'true';

      checkoutButton.addEventListener('click', (event) => {
        const hasSoldOut = this.checkSoldOutItems();

        if (hasSoldOut) {
          event.preventDefault();

          const warning = drawer.querySelector('.grizz-cart-sold-warning');

          if (warning) {
            warning.scrollIntoView({ behavior: 'smooth', block: 'center' });
            warning.classList.add('is-shaking');

            setTimeout(() => {
              warning.classList.remove('is-shaking');
            }, 550);
          }

          return false;
        }
      });
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.drawer__footer'];

          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);

            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (typeof cartDrawer.bindGrizzCartActions === 'function') cartDrawer.bindGrizzCartActions();
            if (typeof cartDrawer.checkSoldOutItems === 'function') cartDrawer.checkSoldOutItems();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      super.onCartUpdate();
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', function () {
  const cartDrawer = document.querySelector('cart-drawer');

  if (cartDrawer) {
    if (typeof cartDrawer.bindGrizzCartActions === 'function') cartDrawer.bindGrizzCartActions();
    if (typeof cartDrawer.checkSoldOutItems === 'function') cartDrawer.checkSoldOutItems();
  }
});